import React, { useEffect, useRef, useState }  from 'react';
// import shaka from 'shaka-player/dist/shaka-player.compiled';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlayCircle, faX } from '@fortawesome/free-solid-svg-icons';

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";

import {isBrowser, isNullable} from "../utilities";

import "shaka-player/dist/controls.css";
import './video-player.less';

/***
 * Video Player Component 
 * 
 * This component makes use of Shaka Player, a Google-developed open-source JavaScript library 
 * for adaptive media. You can find it at https://github.com/shaka-project/shaka-player?tab=readme-ov-file 
 * and technical documentation in https://shaka-player-demo.appspot.com/docs/api/index.html.
 * 
 * The implementation below is based on the Basic Usage examples describe in the respective documentation 
 * tab: https://shaka-player-demo.appspot.com/docs/api/tutorial-basic-usage.html
 */
const VideoPlayerView = (props) => {
  let ref = useRef();

  // GT Portugal HLS Video URL: 'https://mygtdevelopment.s3.eu-west-2.amazonaws.com/gtwebsite/welcomevideo2/index.m3u8'
  const manifestUri = !isNullable(props.data.video_url) ? props.data.video_url : null ;
  const teaserManifestUri =  !isNullable(props.data.teaser_url) ? props.data.teaser_url : null ;

  const title = !isNullable(props.data.title) ? props.data.title : null;
  const teaserCoverImage = !isNullable(props.data.teaser_cover_image) && !isNullable(props.data.teaser_cover_image[0]) ? props.data.teaser_cover_image[0]["getURL"] + "/@@images/image" : "//shaka-player-demo.appspot.com/assets/poster.jpg";
  const videoCoverImage = !isNullable(props.data.video_cover_image) && !isNullable(props.data.video_cover_image[0]) ? props.data.video_cover_image[0]["getURL"] + "/@@images/image" : "//shaka-player-demo.appspot.com/assets/poster.jpg";
  const subtitlesUrl = !isNullable(props.data.subtitles_url) ? props.data.subtitles_url : null;

  const videoRef = useRef(null);
  const teaserRef = useRef(null);
  const descriptionRef = useRef(null);

  const [mainPlayer, setMainPlayer] = useState(null);
  const [teaserPlayer, setTeaserPlayer] = useState(null);
  const [teaserPlayButtonPosition, setTeaserPlayButtonPosition] =  useState(null);

  const visible = (props.data.hidden==true) ? false : true;
  const editEnabled = (props.data.editEnabled==true) ? true : false;
  const withTeaser = (!isNullable(props.data.teaser_url)) ? true : false;

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalClass, setModalClass] = useState('');

  async function initPlayer(manifest=manifestUri, teaser = false, reference, withSubtitles = false) {
    // Create a Player instance.
    const shaka = require('shaka-player/dist/shaka-player.ui.js');
    // const shaka = require('shaka-player/dist/compiled.debug.js');

    console.log(`initPlayer from manifest @ ${manifest}`);
    
    shaka.polyfill.installAll();

    // shaka.log.setLevel(shaka.log.Level.DEBUG);

    const player = new shaka.Player(reference.current);

    

    if (reference == videoRef) {
      try {
        const videoTag =  reference.current;
        const videoContainer = videoTag.parentNode;
        try {
          const ui = new shaka.ui.Overlay(player, videoContainer, videoTag);
          const uiConfig = {
            addSeekBar: true,
            addBigPlayButton: true,
            volume: false,
            controlPanelElements: ['time_and_duration', 'spacer', 'spacer', 'volume', 'captions', 'fullscreen']
          }
          ui.configure(uiConfig);
        } catch (uiEx) {
            console.log(`Error in UI Config:`, uiEx);
        }
        
      } catch (ex) {
        console.log(`ex is `, ex);
      }
      
    }

    // attach player to specific <video> tag, accessed to via a useRef reference received in params
    await player.attach(reference.current); // used to be player.attach(video)

    // Listen for error events.
    player.addEventListener('error', (event) => {
      // Extract the shaka.util.Error object from the event.
      onError(event.detail);
    });

    // This is suboptimal but works and avoids proliferation of player instances,  
    // in case that results in memory issues. 
    if (teaser) {
      // Here we're instantiating the teaser player. It should only ever happen once.
      setTeaserPlayer(player);
    } else {
      // In this case we're about to create a main video player.
      // We do that everytime the user clicks the teaser video or image and the 
      // player overlay modal is created. For that reason, we 
      // destroy the previous player if it exists
      if (!isNullable(mainPlayer)) mainPlayer.destroy();
      
      // Now we store the new player as state;
      setMainPlayer(player);
    }
    

    // Try to load a manifest.
    // This is an asynchronous process.
    try {
      await player.load(manifest);
      // console.log(`******** Has Subtitles`)
      if (withSubtitles) {
        // SUBTITLES ON 
        // CHECK OUT https://scriptme.io/, used by Warner Bros, ITV, Greenpeace, Decathlon, etc
        player.addTextTrackAsync(subtitlesUrl, 'en', 'subtitles', 'text/vtt')
          .then((subtitleTrack) => {
            // const textTracks = player.getTextTracks();
            player.selectTextTrack(subtitleTrack);
            player.setTextTrackVisibility(true);
          })
          .catch((exception) => {
            console.log(`exception in addTextTrackAsync:`, exception);
          })
    }
      // This runs if the asynchronous load is successful.
    } catch (error) {
      // onError is executed if the asynchronous load fails.
      console.error('Video Player Error: ', error.code, 'object', error);
    }
  }

  const setupScrollEffects = () => {
    console.log(`setupScrollEffects CALLED`);
    const videoLoadingContext = gsap.context(() => {
      gsap.timeline()
              .to(".withTeaser", {
                scrollTrigger: {
                  markers: false,
                  trigger: ".withTeaser",
                  start: `top bottom`,
                  end: (self) => `top -100px`,
                  onEnter: (self) => {
                    // console.log(`setupScrollEffects onEnter`);
                    if (!isNullable(teaserRef.current)) teaserRef.current.play();
                  },

                  onEnterBack: (self) => {
                    // console.log(`setupScrollEffects onEnterBack`);
                    if (!isNullable(teaserRef.current)) teaserRef.current.play();
                  },

                  onLeave: (self) => {
                    // console.log(`setupScrollEffects onLeave`)
                    if (!isNullable(teaserRef.current)) teaserRef.current.pause();
                  },
      
                  onLeaveBack: (self) => {
                    // console.log(`setupScrollEffects onLeaveBack`);
                    if (!isNullable(teaserRef.current)) teaserRef.current.pause();
                  }

                }
              })
    }, ref);
  }

  const openModal = () => {
    setModalIsOpen(true);
  }

  // const afterOpenModal = () => {
  //   // references are now sync'd and can be accessed.
  //   // subtitle.style.color = '#f00';
  //   console.log(`on afterOpenModal`)
  // }

  const closeModal = () => {
    // console.log(`closeModal called`);
    setModalIsOpen(false);
  }

  const getPositioningClass = () => {
    return !isNullable(props.data.positioning) ? props.data.positioning : "";
  }

  const getDescriptionTag = () => {
    return !isNullable(props.data.description) ? <div ref={descriptionRef} className='videoDescription'>{props.data.description}</div> : <></>
  }

  const getTitleTag = () => {
    // console.log(`positioning is`, props.data);
    if (!isNullable(props.data.title)) {
      switch (props.data.positioning) {
        case "hero":
          return <h1 className='videoTitle'>{props.data.title}</h1>
        
          case "section":
            return <h2 className='videoTitle'>{props.data.title}</h2>
  
          case "overlay"  :
            return <h3 className='videoTitle'>{props.data.title}</h3>
          
          default:
            return <h2 className='videoTitle'>{props.data.title}</h2>
      }  
    } else {
      return <div className='empty'></div>;
    }
    
    // if (props.data.positioning == )
  }

  useEffect(() => {
  
    // console.log(`useEffect[]`, props);

    // Check to see if the browser supports the basic APIs Shaka needs.
    if (isBrowser()) {
      console.log(`useEffect[] withTeaser=${withTeaser}; \n***teaserManifestUri=${teaserManifestUri} ***\n`, props.data);

      // Everything looks good!
      // shaka.polyfill.installAll();
      if (withTeaser && isNullable(teaserPlayer)) {
        initPlayer(teaserManifestUri, true, teaserRef);
        setupScrollEffects();
      }
      // initPlayer(manifestUri, false, videoRef);
    } else {
      // This browser does not have the minimum set of APIs we need.
      console.error('Browser not supported!');
    }

    try {
      const rect = descriptionRef.current.getBoundingClientRect()
      // console.log(rect.top, rect.right, rect.bottom, rect.left)
      const playButtonPosition = {
        right: rect.right + 20,
        bottom: rect.bottom - 20
      }
      setTeaserPlayButtonPosition(playButtonPosition)
    } catch (ex) {

    }

    return () => {
      if (!isNullable(teaserPlayer)) teaserPlayer.destroy();
      if (!isNullable(mainPlayer)) mainPlayer.destroy();
    };
    
  }, []);


  useEffect(() => {
    // console.log(`useEffect[modalIsOpen]. state is ${modalIsOpen}`)
    const modalClassName = (modalIsOpen) ? 'open' : 'closed';
    setModalClass(modalClassName);

    if (modalIsOpen) {
      try {
        teaserRef.current.pause();
      } catch (ex) {
        // video not yet set, so can't play. ignore exception
      }

      if (isNullable(mainPlayer)) {
        console.log(`init main video!`)
        initPlayer(manifestUri, false, videoRef, subtitlesUrl!=null);
      } else {
        console.log(`should restart playing main video!`)
        initPlayer(manifestUri, false, videoRef, subtitlesUrl!=null);
        // videoRef.current.play();
      }
      
      
    } else {
      try {
        teaserRef.current.play();
      } catch (ex) {
        // video not yet set, so can't play. ignore exception
      }
      
    }
    
  }, [modalIsOpen]);

  const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
    },
  };

  const getPlayButtonStyle = () => {
    try {
      return {
        left: teaserPlayButtonPosition.left, 
        bottom: teaserPlayButtonPosition.bottom
      }
    } catch (e) {
      return {}
    }
  }

  return( 
    <div className={`videoPlayerBlock ${getPositioningClass()}`} ref={ref}>
      {(visible || editEnabled) && 
          <div className='video-player-block-content-container' >
              <div className='darkenOverlay'></div>
              <div className='video-player-block-text-container'>
                  {getTitleTag()}
                  {getDescriptionTag()}
                  {!isNullable(props.data.video_url) && 
                    <div className='teaserPlayButton' onClick={openModal} > 
                      <div className='videoCTA'>
                          <span className="cta_text">{props.data.cta}</span> 
                          <span className="cta_button"><FontAwesomeIcon icon={faPlayCircle} /></span>
                      </div>
                    </div>
                  }
                  
              </div>  
              
              {(!withTeaser) && 
              <div className="videoPlayerContainer noTeaser">
                <div className='videoPoster'><img src={videoCoverImage}/></div>
              
                {
                  (modalIsOpen) && 
                  <div className={`modalContainer ${modalClass}`}>
                    <div className='modalContent'>
                      <div className="modalHeader" onClick={closeModal}>
                        <div className='modelHeaderContentContainer'></div>
                        <div className='modelHeaderCloseButton'> <FontAwesomeIcon icon={faX}/></div>
                       
                      </div>

                      <div className='mainVideoContainer'>
                        <video id="mainVideoPlayer" 
                            ref={videoRef}
                            width="640"
                            poster={videoCoverImage}/>
                      </div>
                    </div>  
                  </div>  
                  
                }
            </div> 
              }
              
              {
                (withTeaser) && 
                  <div className="videoPlayerContainer withTeaser">
                    <video ref={teaserRef}
                      className="videoTeaser"
                      width="640"
                      poster={teaserCoverImage}
                      muted loop></video>
                      {
                        (modalIsOpen) && 
                        <div className={`modalContainer ${modalClass}`}>
                          <div className='modalContent'>
                            <div className="modalHeader" onClick={closeModal}>
                              <div className='modelHeaderContentContainer'></div>
                              <div className='modelHeaderCloseButton'> <FontAwesomeIcon icon={faX}/></div>
                             
                            </div>

                            <div className='mainVideoContainer'>
                              <video id="mainVideoPlayer" 
                                  ref={videoRef}
                                  width="640"
                                  poster={videoCoverImage}/>
                            </div>
                          </div>  
                        </div>  
                        
                      }
                  </div>    
              }
              
          </div>  
      } 
    </div>
  )
};

export default VideoPlayerView;